import React, { useMemo, useState } from 'react';

import {
  Flex,
  Text,
  Image,
  HStack,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useMutation } from 'react-query';

import { updateDocument } from 'api/activities';
import { Carousel, FileInput, Icon } from 'components';
import CarouselModal from 'containers/Modals/Carrousel';
import SectionForm from 'containers/Section/Form';
import { ContentRoutes } from 'enums/routes';
import { AccessTypes, ActivityTypes, MethodTypes } from 'enums/types';
import { policy } from 'polices';
import useRouteStore from 'stores/useRoute';

import AirportInfo from './AirportInfo';
import GeneralInfo from './GeneralInfo';
import SectionTable from './Table';
import TranshipmentInfo from './TranshipmentInfo';

function Signature({ url, name, documentNumber }) {
  const [openModal, setOpenModal] = useState<boolean>(false);

  if (!url) {
    return null;
  }
  return (
    <Flex
      ml={2}
      alignItems="center"
      flexDir="column">
      <CarouselModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        images={[url]}
      />
      <Image
        onClick={() => setOpenModal(true)}
        objectFit="cover"
        border="1px"
        borderColor="gray.150"
        borderRadius={2}
        w="142px"
        h="67px"
        src={url}
      />
      <Text
        variant="2xSmallSemiBold"
        color="gray.500">
        {`${name} - ${documentNumber}`}
      </Text>
    </Flex>
  );
}

function ActivityInfo({
  isExecution, data, onSuccess = () => null, isFetching = false,
}) {
  const { query } = useRouteStore();
  const registers = useMemo(() => [...new Set([
    ...data.imageUrls ?? [],
    ...data.initialOdometerUrls ?? [],
    ...data.finalOdometerUrls ?? [],
    ...data.followupInitialOdometerUrls ?? [],
    ...data.followupFinalOdometerUrls ?? [],
  ])], [data]);

  const Info = useMemo(() => ({
    [ActivityTypes.Collect]: GeneralInfo,
    [ActivityTypes.Delivery]: GeneralInfo,
    [ActivityTypes.Dispatch]: AirportInfo,
    [ActivityTypes.Transhipment]: TranshipmentInfo,
  }[data.type]), [data]);

  const { mutate } = useMutation(async ({ url, name }: any) => {
    await updateDocument(data.id, {
      name,
      url,
    });
  }, {
    onSuccess: () => {
      onSuccess();
      query.refetch();
    },
  });
  if (!isExecution) {
    return (
      <Info
        data={data}
        isExecution={isExecution}
      />
    );
  }

  return (
    <>
      <Info
        data={data}
        isExecution={isExecution}
      />
      <SectionTable
        my={6}
        data={data}
      />
      {!isEmpty(registers) && (
      <Flex
        justify="space-between">
        <Text>Registros:</Text>
        <Carousel
          mb={5}
          images={registers}
        />
      </Flex>
      )}
      <Flex
        mb={5}
        hidden={isEmpty(data.signature)}
        justify="space-between">
        <Text>Autenticação:</Text>
        <Flex>
          {data.signature && <Signature {...data.signature} />}
        </Flex>
      </Flex>
      <Flex
        hidden={data.type !== ActivityTypes.Dispatch}
        justifyContent="space-between"
        w="100%">
        <Text>Anexos:</Text>
        <HStack>
          <FileInput
            isFetching={isFetching}
            isEmpty={!data.documents?.find(({ label }) => label === 'Minuta')}
            multiple={false}
            onChange={(url: string) => mutate({ url, name: 'Minuta' })}
            name="Minuta"
            accept="application/pdf"
            value={data.documents?.find(({ label }) => label === 'Minuta')?.url}
          />
          <FileInput
            isFetching={isFetching}
            isEmpty={!data.documents?.find(({ label }) => label === 'Declaração de conteúdo')}
            multiple={false}
            onChange={(url: string) => mutate({ url, name: 'Declaração de conteúdo' })}
            name="Declaração de conteúdo"
            accept="application/pdf"
            value={data.documents?.find(({ label }) => label === 'Declaração de conteúdo')?.url}
          />
        </HStack>
      </Flex>
    </>
  );
}

interface SectionContentProps {
  data?: any
  index?: number
  isExecution?: boolean
  isFetching?: boolean
  isNew?: boolean
  onClose?: () => void
  onSuccess?: () => void
}

export default function SectionContent({
  data,
  index,
  isExecution = false,
  isNew = false,
  isFetching = false,
  onClose = () => null,
  onSuccess = () => null,
}:SectionContentProps) {
  const [isEditing, setIsEditing] = useState(false);
  const policies = policy(ContentRoutes.Routes);

  return (
    <Flex
      width="100%"
      flexDir="column">
      {policies(MethodTypes.Update) !== AccessTypes.Disallow
          && (
          <Flex
            justifyContent="flex-end"
            w="100%">
            <Icon
              hidden={isEditing || isExecution}
              onClick={() => setIsEditing(true)}
              cursor="pointer"
              size="xs"
              color="gray.500"
              position="relative"
              right={-9}
              name="edit"
            />
          </Flex>
          )}
      {isEditing || !data
        ? (
          <SectionForm
            isNew={isNew}
            index={index}
            data={data}
            onClose={onClose}
            onCancel={() => setIsEditing(false)}
            onSuccess={onSuccess}
          />
        )
        : (
          <ActivityInfo
            isExecution={isExecution}
            isFetching={isFetching}
            data={data}
            onSuccess={onSuccess}
          />
        )}
    </Flex>
  );
}
